import { Button, FormLayout, Heading, Link, PageContainer, Paragraph } from '@expressable/syntax-ds';
import { captureException } from '@sentry/react';
import { DocumentTitle } from 'components/DocumentTitle';
import { ExpressablesFooter } from 'components/Layout/AuthenticationLayout';
import config from 'config';
import { onClearSignupLocalStorage } from 'domains/signupDirect/misc/localStorage';
import { HeadingCombo } from 'domains/signupDirect/SyntaxComps/HeadingCombo';
import { noop } from 'lodash';
import React, { FC } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';
import { useEffectOnce } from 'react-use';
import { routes } from 'router/routes';
import { formatPhoneNumber } from 'utils/phoneNumber';

const ErrorFallback: FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  const navigate = useNavigate();
  const handleReset = () => {
    onClearSignupLocalStorage();
    resetErrorBoundary();
    navigate(routes.signupDirect.base);
  };

  useEffectOnce(() => {
    captureException({
      message: '[SignupConsolidated] :: Error boundary triggered',
      data: {
        error,
      },
      level: 'error',
    });

    // eslint-disable-next-line no-console
    console.warn(`Signup error boundary triggered`, {
      error,
    });
  });
  const { t } = useTranslation();

  const signupManualPhoneNumber = config.get('signupManualPhoneNumber') as string;
  const signupManualEmail = config.get('signupManualEmail') as string;
  return (
    <>
      <DocumentTitle>{t('screener.pageTitle')}</DocumentTitle>
      <PageContainer
        variant="form"
        className="mobile-d:px-e24 mobile-d:my-e40 mobile-xs-d:mt-e16 tablet-d:gap-e32 mobile-xs-d:gap-e24 items-stretch  max-w-[600px]"
      >
        <FormLayout onSubmit={noop} className="text-center items-center">
          <HeadingCombo>
            <Heading noDefaultMargins level="h2">
              Something went wrong
            </Heading>
            <Paragraph variant="secondary">
              This may be an intermittent problem with our systems. Please continue signing up by emailing us at{' '}
              <Link external={true} to={`mailto:${signupManualEmail}`}>
                {signupManualEmail}
              </Link>
              . You can also{' '}
              <Link external={true} to={`tel:${signupManualPhoneNumber}`}>
                call us
              </Link>{' '}
              or try again
            </Paragraph>
          </HeadingCombo>

          <FormLayout.CTA>
            <div className="flex items-center justify-center w-full gap-e24 mobile-d:flex-col">
              <Button onClick={handleReset} size="lg">
                Start over
              </Button>
              <Button as="a" href={`tel:${signupManualPhoneNumber}`} variant="link">
                Call us at {formatPhoneNumber(signupManualPhoneNumber)}
              </Button>
            </div>
          </FormLayout.CTA>
        </FormLayout>
      </PageContainer>
      <ExpressablesFooter />
    </>
  );
};

const SignupErrorBoundary: React.FC = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Outlet />
    </ErrorBoundary>
  );
};

export default SignupErrorBoundary;
